import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { buildLocalizedPath, type Lang } from "~/config/route-config";
import { cn } from "~/lib/cn";
import type { NavSessionUser } from "~/lib/nav-session";
import { getMemberNavActiveKey } from "~/lib/member-nav-region";
import { useLibrarySignal } from "~/store/library-signal";
import { AppLink } from "~/components/ui/AppLink";
import { NavPendingSweep } from "~/components/ui/NavPendingSweep";
import { LibSeal, useSealPresence } from "~/components/layout/LibrarySeal";
import { AvatarMenu } from "~/components/layout/AvatarMenu";
import { useHeaderScrolled } from "~/components/layout/use-header-scrolled";
import { startsInAppNavigation, useNavPendingTo } from "~/components/layout/use-nav-pending";

interface MemberHeaderProps {
  sessionUser: NavSessionUser;
  /**
   * T102 — route renders a dark full-bleed hero: the bar takes the `.dark`
   * treatment at top and snaps to the light scrolled treatment past the
   * threshold. Same contract as `AppHeader`; a socia who opens a dark-hero
   * public surface (`/es` and `/es/auto-test` are the two that set it) otherwise
   * gets a cream bar cutting across it.
   */
  darkAtTop?: boolean;
  className?: string;
}

/**
 * Member global header — T98 `.appbar` (sticky, light). Desktop: wordmark +
 * inline links (Mi espacio · Biblioteca) + reserved bell + avatar pill. Mobile
 * collapses to wordmark + bare avatar (tab bar carries the destinations).
 */
export function MemberHeader({ sessionUser, darkAtTop, className }: MemberHeaderProps) {
  const { t, i18n } = useTranslation("nav");
  const { pathname } = useLocation();
  const active = getMemberNavActiveKey(pathname);
  const scrolled = useHeaderScrolled();

  /* T558 — the desktop counterparts of the two tabs T556 lit on mobile. The tab bar is
     `<768` only, so on a laptop the identical tap was still landing in silence: same two
     destinations, same server hop, no acknowledgment. `aria-current` stays where it is
     and `aria-busy` carries the wait (R-T558-5). */
  const lang = (i18n.language === "en" ? "en" : "es") as Lang;
  // T560 — `onNavigate` is what makes THIS link the one that lights. Without it the
  // header lit for any navigation to the dashboard, including one the phase screen's
  // own back control started, because both resolve the same path.
  const { pending: pendingDashboard, onNavigate: startDashboard } = useNavPendingTo(
    buildLocalizedPath("dashboard", lang),
  );
  const { pending: pendingLibrary, onNavigate: startLibrary } = useNavPendingTo(
    buildLocalizedPath("library", lang),
  );

  // «La palabra que espera» (T297 v2) — the word + glint on the armed
  // library link; the presence hook keeps it mounted for the T307 beat.
  const pendingTechniques = useLibrarySignal();
  const armed = pendingTechniques.length > 0;
  const { mounted: sealMounted, clearing: sealClearing } = useSealPresence(armed);

  return (
    <header
      className={cn(
        "appbar appbar--member sticky top-0 z-50",
        scrolled && "is-scrolled",
        darkAtTop && "over-dark",
        darkAtTop && !scrolled && "dark",
        className,
      )}
    >
      <div className="left">
        <AppLink routeKey="dashboard" aria-label={t("home_aria")} className="wordmark">
          The<span className="fitt text-gradient-tfc">Fitt</span>Club
        </AppLink>
        <nav className="links" aria-label={t("primary_label")}>
          <AppLink
            routeKey="dashboard"
            className={cn("link", pendingDashboard && "nav-pending")}
            prefetch="intent"
            aria-current={active === "dashboard" ? "page" : undefined}
            aria-busy={pendingDashboard || undefined}
            onClick={(e) => { if (startsInAppNavigation(e)) startDashboard(); }}
          >
            {t("links.dashboard")}
            {pendingDashboard ? <NavPendingSweep /> : null}
          </AppLink>
          <AppLink
            routeKey="library"
            className={cn("link", sealClearing && "is-clearing", pendingLibrary && "nav-pending")}
            prefetch="intent"
            aria-current={active === "library" ? "page" : undefined}
            aria-busy={pendingLibrary || undefined}
            onClick={(e) => { if (startsInAppNavigation(e)) startLibrary(); }}
          >
            {sealMounted ? (
              <span className="lib-link-shimmer">{t("links.library")}</span>
            ) : (
              t("links.library")
            )}
            {sealMounted ? <LibSeal clearing={sealClearing} /> : null}
            {pendingLibrary ? <NavPendingSweep /> : null}
          </AppLink>
        </nav>
      </div>

      <div className="right">
        <AvatarMenu sessionUser={sessionUser} />
      </div>
    </header>
  );
}
